import React, { useEffect, useCallback, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/rootDashboard.css";
import RootHeader from "../components/RootHeader";
import RootKpis from "../components/RootKpis";
import RootChartsSection from "../components/RootChartsSection";
import RootAssignmentsSection from "../components/RootAssignmentsSection";
import CampusSessionsSection from "../components/CampusSessionsSection";
import RootResetsSection from "../components/RootResetsSection";
import RootLogsConsole from "../components/RootLogsConsole";
import useSecurityDashboard from "../hooks/useSecurityDashboard";
import useFrontendLogs from "../hooks/useFrontendLogs";
import securityDashboardService from "../services/securityDashboardService";
import { showToast } from "../../../../utils/showToast";
import { getSocket, initSocket } from "../../../../utils/socketClient";

export default function RootDashboardView() {
  const { data, loading, error, fetchDashboard } = useSecurityDashboard();
  const {
    logs,
    loading: logsLoading,
    error: logsError,
    refresh: refreshLogs,
  } = useFrontendLogs();

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const refreshTimeoutRef = useRef(null);
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) return;
    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchDashboard();
    }, 350);
  }, [fetchDashboard]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;
    const socket = getSocket() || initSocket(token);
    if (!socket) return undefined;

    const onSessionsChanged = () => {
      scheduleRefresh();
    };

    socket.on("seguridad:sesiones-actualizadas", onSessionsChanged);

    return () => {
      socket.off("seguridad:sesiones-actualizadas", onSessionsChanged);
    };
  }, [scheduleRefresh]);

  const kpis = data?.kpis || [];
  const charts = data?.charts || {};
  const sessionsByDay = charts.sessionsByDay || [];
  const activityByDay = charts.activityByDay || [];
  const activeSessions = data?.activeSessions || [];
  const campusSessions = data?.campusSessions || [];
  const usersMissingAssignments = data?.usersMissingAssignments || [];
  const pendingResets = data?.pendingResets || [];
  const activeSessionsCount = activeSessions.filter((row) => row.activa).length;

  return (
    <section className="management-page rootDashboard">
      <RootHeader lastUpdate={data?.lastUpdate} loading={loading} onRefresh={fetchDashboard} />

      {error && (
        <div className="management-alert rootDashboard-alert mb-4">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <RootKpis kpis={kpis} />
      <RootChartsSection sessionsByDay={sessionsByDay} activityByDay={activityByDay} />
      <RootAssignmentsSection
        activeSessions={activeSessions}
        activeSessionsCount={activeSessionsCount}
        usersMissingAssignments={usersMissingAssignments}
        onExpireSession={async (sessionId) => {
          if (!sessionId) return;
          try {
            const response = await securityDashboardService.expireSession(sessionId);
            showToast({ type: "success", message: response?.message || "Sesión caducada correctamente." });
            fetchDashboard();
          } catch (error) {
            showToast({ type: "danger", message: error?.message || "No se pudo caducar la sesión." });
          }
        }}
        onSetTemporaryPassword={async (sessionId, password) => {
          if (!sessionId || !password) return;
          try {
            const response = await securityDashboardService.setTemporaryPassword(sessionId, password);
            showToast({ type: "success", message: response?.message || "Contraseña temporal aplicada." });
            fetchDashboard();
          } catch (error) {
            showToast({ type: "danger", message: error?.message || "No se pudo aplicar la contraseña temporal." });
          }
        }}
      />
      <CampusSessionsSection
        campusSessions={campusSessions}
        onExpireSession={async (sessionId) => {
          if (!sessionId) return;
          try {
            const response = await securityDashboardService.expireCampusSession(sessionId);
            showToast({ type: "success", message: response?.message || "Sesión campus caducada." });
            fetchDashboard();
          } catch (error) {
            showToast({ type: "danger", message: error?.message || "No se pudo caducar la sesión campus." });
          }
        }}
      />
      <RootResetsSection pendingResets={pendingResets} />
      <RootLogsConsole
        logs={logs}
        loading={logsLoading}
        error={logsError}
        onRefresh={refreshLogs}
      />
    </section>
  );
}
